/* eslint-disable @typescript-eslint/naming-convention */
import { LazyLoadEvent } from 'primeng/api';

export interface ITableV2Config {
  vendorGroup?: string;
  /** @filterVendorByConditionType: set query property  by vendor condition type **/
  filterVendorByConditionType?: string;
  /** @pageType: required to page specific translation data  **/
  pageType?: string;
  /** @toolTip: required to page specific translation data  **/
  toolTip?: ITableV2ToolTip;
  /** @selectedToolTip: selected tooltip data by filter dropdown **/
  selectedToolTip?: string;
  /** @totalRecords:  length of total records  **/
  totalRecords?: number;
  /** @column:  list of all columns to display in table **/
  columns?: ITableColumns[];
  /** @activeColumns:  list of active columns to display in table **/
  activeColumns?: ITableColumns[];
  /** @meta:  table pagination configuration **/
  meta?: ITableMetaData;
  /** @actions:  List of events to display buttons in action column
   * If using actions, the view event will be required by default. because we will disable row selection option,
   * and button will be used for view record
   * **/
  actions?: ITableActions[];
  /** @defaultSortingField:  set default sorting column name **/
  defaultSortingField: string;
  /** @searchText:  set default sorting column name **/
  searchText?: string;
  /** @actionLabel:  set actions column label **/
  actionLabel?: string;
  /** @inputPlaceholder:  set place holder text for input**/
  inputPlaceholder?: string;
  /** @isLoading:  set boolean value to show hide loading **/
  isLoading?: boolean;
  /** @rowHover:  set table row style on mouse hover **/
  rowHover?: boolean;
  /** @isEditable:  set permission for the row if it's editable **/
  isEditable?: boolean;
  /** @rows:  number of rows to be displayed per page **/
  rows?: number;
  /** @sortMode:  will be used for sorting with single multi columns **/
  sortMode?: 'single' | 'multiple';
  /** @limit:  number of records to be displayed per page **/
  limit?: string | number;
  /** @isDescOrder:  sort column with  ASC/DESC  order by default **/
  isDescOrder?: boolean;
  /** @showCurrentPageReport:  will be set true by default to show pagination details **/
  showCurrentPageReport?: boolean;
  /** @selectedRecord:  use to hold selected data **/
  selectedRecord?: any;
  /** @defaultFilterColumns: use to provide column details for filter by dropdown**/
  defaultFilterColumns?: ITableColumns;
  /** @visibleDataLazyLoadingTable: show/hide new table base on data length on page loading  **/
  visibleDataLazyLoadingTable?: boolean;
  /** @tab: set activated tab **/
  tab?: string;
  /** @tab: set activated tab **/
  selectedDate?: string;
  /** @hasVendor: check if  territory has vendor to assign otherwise disable assign sales rep button **/
  hasVendor?: boolean;
  /** @selectedOperator: set operator based on column type  **/
  selectedOperator?: string;
  /** @highlightRow: Set record ID to highlight row on map hover**/
  highlightRow?: number;
  /** @dataNotFound: Table data not found message **/
  dataNotFound?: string;
  /** @isAutoCompleteHidden: hide search bar **/
  isAutoCompleteHidden?: boolean;
  /** @enableExportData: set to show export data button: note currently only for walklists */
  enableExportData?: boolean;
  exportDataToolTip?: string;
  /** @allColumns:  mapped columns label from translation **/
  allColumns?: ITableColumns[];
  /** @multipleselection: enables checkboxes on table for multiple selection **/
  multipleSelection?: boolean;
}

export interface ITableColumns {
  label?: string;
  /**
   * @field: property name
   * @note: for actions column need to add array of  events like ['delete', 'edit']. it will render action
   * buttons dynamically
   * **/
  field?: string;
  type?: string;
  isCheckbox?: boolean;
  isDate?: boolean;
  isText?: boolean;
  /** @visible:  set true or false to show | hide columns**/
  visible?: boolean;
  /** @isAssignee:  will be used to assign sales rep **/
  isAssignee?: boolean;
  /**  @disabledFilter:  set true if you want to disable sorting functionality of particular column **/
  disabledSorting?: boolean;
  /**  @isProduct:  set true  if column is existing products **/
  isProduct?: boolean;
  /**  @isAddress:  set true if column is address  **/
  isAddress?: boolean;
  /**  @isLastKnock:  set true if column is last knock status **/
  isLastKnock?: boolean;
  /**  @isTechnology:  set true if column is available and existing technology column  **/
  isTechnology?: boolean;
  /** @disabledFilter:  set true if you want to disable filter functionality of particular column **/
  disabledFilter?: boolean;
}

export interface ITableV2EventConfig {
  view?: ITableActions;
  delete?: ITableActions;
  edit?: ITableActions;
  redirectLink?: ITableActions;
}
export interface ITableV2ToolTip {
  text?: string;
  date?: string;
  checkbox?: string;
}

export interface ITableActions {
  name: 'view' | 'delete' | 'edit' | 'redirectLink';
  icon?: string;
  color?: string;
}

export interface ILazyLoadingEvent extends LazyLoadEvent {
  filter?: string;
  first?: number;
  operator?: string;
  page?: number;
  limit?: number | string;
  sort?: string;
  pageCount?: number;
  rows?: number;
  eventType?: string;
  filterBy?: string;
}

export interface ITableMetaData {
  totalItems?: number;
  itemCount?: number;
  itemsPerPage?: number;
  totalPages?: number;
  currentPage?: number;
}

export enum TABLE_V2_SORTING_COLUMNS {
  TERRITORY_NAME = 'territoryName',
  WALKLIST_PIN = 'walklistPin',
  WALKLIST_NAME = 'walklistName',
  WALKLIST_TERRITORY_ID = 'territoryId',
  WALKLIST_ID = 'id',
  TERRITORY_ID = 'id',
  ID = 'id',
}

export enum SORT_ORDER {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum TV2_QUERY_OPERATOR {
  BETWEEN = '$between',
  CONT = '$cont',
  STARTS = '$starts',
  EQ = '$eq',
  GT = '$gte',
}

export enum TABLE_V2_MODAL_CONFIG {
  CSS_CLASS = 'telus-modal-bg',
}

export enum GEOMETRY_TYPES {
  POLYGON = 'Polygon',
  EPSG_4326 = 'EPSG:4326',
  EPSG_3857 = 'EPSG:3857',
}

export enum TAB_SEGMENTS {
  OVERVIEW = 'overview',
}

export enum CURRENT_TECH {
  F = 'Fibre',
  C = 'Copper',
  //S = 'Sattelite',    // No need to display because d2c1 is not displaying at current tech column
  //M = 'Mobile',       // No need to display
  W = 'Smart Hub',
}
export enum AVAILABLE_TECH {
  F = 'Fibre',
  C = 'Copper',
  //S = 'Sattelite',    // No need to display
  //M = 'Mobile',       // No need to display
  W = 'Smart Hub',
}

export enum CURRENT_TECH_KEYS {
  fibre = 'F',
  copper = 'C',
  whsia = 'W',
}

export enum AVAILABLE_TECH_KEYS {
  fibre = 'F',
  copper = 'C',
  whsia = 'W',
}

export enum TABLE_COL_PROPERTIES {
  addressId = 'addressId',
  createdBy = 'createdBy',
  modifiedBy = 'modifiedBy',
  vendorAccountName = 'vendorAccountName',
  vendorGroup = 'vendorGroup',
  outletId = 'outletId',
  orgId = 'orgId',
}

export enum TECH_PROPERTIES {
  currentTechnologyType = 'currentTechnologyType',
  availableTechnologyType = 'availableTechnologyType',
  existingProduct = 'existingProduct',
}

export enum VENDOR_CONDITIONS {
  vendorLead = 'user.vendor.vendorGroupId',
  vendorManager = 'user.vendor.vendorAccountName',
}

export enum PAGE_TYPES {
  'knock-list' = 'knock',
  'drop-permission' = 'drop',
}
